import {
  FORBIDDEN_TAGS,
  RICH,
  SAFE_IMAGE_DATA_URI,
  SAFE_URL_SCHEMES,
  URL_ATTRIBUTES,
} from './html-verification';

/**
 * Sanitizes consumer-supplied HTML strings before they are marked as trusted.
 *
 * The SPDS components that accept an `@content` string mark it html-safe so authors can use HTML
 * for text formatting, which makes any user-controlled value reaching them a stored-XSS vector.
 * This module removes everything outside the formatting subset the components actually promise,
 * under one of the policies defined in `html-verification` (`STRICT` for tooltips, `RICH` for
 * containers and modals).
 *
 * Two properties are load-bearing:
 *
 * 1. **Allowlist, never denylist.** An element or attribute survives only by being named in the
 *    policy, so new attack surface (a novel event-handler attribute, say) is excluded by default
 *    rather than by a pattern someone has to remember to update.
 * 2. **Fail closed.** Anything the sanitizer cannot reduce to a stable result -- or cannot parse at
 *    all -- comes back HTML-escaped, rendering as visible text instead of markup.
 */

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

/**
 * Sanitization runs until its output stops changing, to defend against mXSS payloads that only
 * become dangerous once the browser re-parses the serialized output. Three passes is enough for any
 * benign input to normalize; input still changing after that is hostile by construction.
 */
const MAX_SANITIZE_PASSES = 3;

const ESCAPE_MAP = Object.freeze({
  '"': '&quot;',
  '&': '&amp;',
  "'": '&#39;',
  '<': '&lt;',
  '>': '&gt;',
});

const ESCAPE_PATTERN = /["&'<>]/g;

/**
 * Control characters and every flavor of whitespace, stripped before URL scheme comparison.
 * The C0 range is matched deliberately, NUL included: it is exactly the noise a payload uses to
 * break up `javascript:`, so `no-control-regex` is inverted here.
 */
// eslint-disable-next-line no-control-regex
const URL_NOISE_PATTERN = /[\u0000-\u0020\u007F-\u00A0\u2028\u2029\uFEFF]+/g;

const URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/;

/** Cached result of the one-time parser feature test. See {@link isParsingSupported}. */
let parsingSupported;

/**
 * Escapes a string so it renders as visible text rather than markup. Used both for the fail-closed
 * paths and wherever parsing is unavailable.
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value).replace(ESCAPE_PATTERN, character => ESCAPE_MAP[character]);
}

/**
 * Whether this environment can parse HTML into an inert fragment.
 *
 * `client-portal` renders through FastBoot, whose SimpleDOM document exposes `createElement` but no
 * `template.content`, so a `typeof document` check alone would let the sanitizer silently no-op
 * there. A missing `document` altogether throws a ReferenceError into the same `catch`, so both
 * no-DOM cases resolve here. The feature test runs once and is cached; when it fails, callers
 * escape instead.
 * @returns {boolean}
 */
function isParsingSupported() {
  if (parsingSupported !== undefined) return parsingSupported;

  try {
    let template = document.createElement('template');

    template.innerHTML = '<b>x</b>';
    parsingSupported = template.content?.childNodes?.length === 1;
  } catch {
    parsingSupported = false;
  }

  return parsingSupported;
}

/**
 * Normalizes a URL for scheme comparison. Control characters, whitespace, and case are all stripped
 * first, so obfuscated payloads such as `java&#9;script:` or a leading newline compare as their
 * canonical form rather than slipping past the allowlist as an unrecognized string.
 *
 * The normalized value is only ever compared, never written back to the attribute.
 * @param {string} value
 * @returns {string}
 */
function normalizeUrl(value) {
  return String(value ?? '')
    .replace(URL_NOISE_PATTERN, '')
    .toLowerCase();
}

/**
 * Whether a URL is safe to keep. Unknown schemes are rejected, so this fails closed.
 * @param {string} value
 * @param {boolean} [allowRasterDataUri] - Set for `img[src]`, which may carry an inline raster
 *   image. Never set for `a[href]`.
 * @returns {boolean}
 */
function isSafeUrl(value, allowRasterDataUri = false) {
  let url = normalizeUrl(value);
  let [scheme] = url.match(URL_SCHEME_PATTERN) ?? [];

  // No scheme is a relative reference (`/path`, `#hash`, `./x`, `bare/path`, or an empty value),
  // which can neither execute script nor name its own scheme.
  if (!scheme) return true;

  if (SAFE_URL_SCHEMES.includes(scheme)) return true;

  return allowRasterDataUri && SAFE_IMAGE_DATA_URI.test(url);
}

/**
 * Replaces an element with its children, keeping its text. Used for elements that are merely not
 * allowed, as opposed to the forbidden ones that are dropped with their subtree.
 * @param {Element} element
 */
function unwrap(element) {
  let parent = element.parentNode;

  if (!parent) return;

  while (element.firstChild) parent.insertBefore(element.firstChild, element);

  parent.removeChild(element);
}

/**
 * Whether the policy allows this attribute on this tag, globally or by name.
 * @param {string} attribute - Already lowercased.
 * @param {string} tag
 * @param {object} policy
 * @returns {boolean}
 */
function isAllowedAttribute(attribute, tag, policy) {
  return (
    policy.globalAttributes.has(attribute) || Boolean(policy.tagAttributes[tag]?.has(attribute))
  );
}

/**
 * Whether an attribute that carries a URL points somewhere safe. Attributes that carry no URL are
 * vacuously safe, so this reads as a single condition at the call site.
 * @param {Element} element
 * @param {string} name - The attribute's name as authored, for reading the value back.
 * @param {string} tag
 * @returns {boolean}
 */
function hasSafeUrlValue(element, name, tag) {
  if (!URL_ATTRIBUTES[tag]?.has(name.toLowerCase())) return true;

  return isSafeUrl(element.getAttribute(name), tag === 'img');
}

/**
 * Removes every attribute not allowlisted for this tag, then validates the URLs on what remains.
 *
 * Because the allowlist is positive, `on*` handlers and `style` are dropped by construction: there
 * is no pattern here for a novel attribute to evade.
 * @param {Element} element
 * @param {string} tag
 * @param {object} policy
 */
function sanitizeAttributes(element, tag, policy) {
  for (let { name } of [...element.attributes]) {
    let attribute = name.toLowerCase();
    let keep = isAllowedAttribute(attribute, tag, policy) && hasSafeUrlValue(element, name, tag);

    if (!keep) element.removeAttribute(name);
  }
}

/**
 * Applies the policy's anchor rules to a surviving `<a>`.
 *
 * Both rules must stay **idempotent**: a second pass has to produce the same attributes, or the
 * stabilization loop in {@link sanitize} never converges and every anchor takes the fail-closed
 * path. They are applied inline rather than through registered hooks, because hooks of the kind
 * `simplepractice/frontend`'s DOMPurify helper registers are global and never removed, so they
 * accumulate across calls and the last one registered wins regardless of which call is running.
 * @param {Element} element
 * @param {object} policy
 */
function rewriteAnchor(element, policy) {
  // Overwrites any existing value, an author's explicit `target="_self"` included, for parity with
  // `simplepractice/frontend/app/helpers/sanitize-html.js`.
  if (policy.forceTargetBlank) element.setAttribute('target', '_blank');

  // Deliberately independent of the flag above: a policy derived from RICH with
  // `forceTargetBlank: false` still hardens anchors that ask for a new tab themselves.
  if (element.getAttribute('target')?.toLowerCase() === '_blank') {
    element.setAttribute('rel', 'noopener noreferrer');
  }
}

/**
 * Applies the policy to an element the policy allows, recursing into its subtree.
 * @param {Element} element
 * @param {string} tag
 * @param {object} policy
 */
function sanitizeAllowedElement(element, tag, policy) {
  sanitizeAttributes(element, tag, policy);

  // After the allowlist and URL validation, so it only touches anchors that survived: forcing
  // `target` earlier would see it stripped again, and attaching `rel` earlier would harden an
  // anchor whose `href` is about to be dropped.
  if (tag === 'a') rewriteAnchor(element, policy);

  sanitizeChildren(element, policy);
}

/**
 * Routes one element to its disposition: forbidden ones go with their subtree, allowed ones are
 * cleaned in place, and the rest are unwrapped so their text survives.
 * @param {Element} element
 * @param {object} policy
 */
function sanitizeElement(element, policy) {
  // `localName` rather than `nodeName`, so a namespace-cased tag such as `<SVG>` resolves to the
  // same key the policy collections use.
  let tag = element.localName.toLowerCase();

  if (FORBIDDEN_TAGS.has(tag)) {
    // Dropped with the subtree, so the text inside a `<script>` cannot leak back out as content.
    element.remove();
  } else if (policy.tags.has(tag)) {
    sanitizeAllowedElement(element, tag, policy);
  } else {
    // Merely not allowed rather than forbidden, so the wrapper goes but the text stays.
    sanitizeChildren(element, policy);
    unwrap(element);
  }
}

/**
 * Applies the policy to one node, recursing into whatever survives.
 * @param {Node} node
 * @param {object} policy
 */
function sanitizeNode(node, policy) {
  if (node.nodeType === ELEMENT_NODE) {
    sanitizeElement(node, policy);
  } else if (node.nodeType !== TEXT_NODE) {
    // Comments and every other non-element node type go, so a comment cannot smuggle markup back
    // through a later parse.
    node.remove();
  }
}

/**
 * Walks a parsed fragment in place, applying the policy to every descendant.
 * @param {Node} node
 * @param {object} policy
 */
function sanitizeChildren(node, policy) {
  for (let child of [...node.childNodes]) sanitizeNode(child, policy);
}

/**
 * Parses, sanitizes, and re-serializes a single pass.
 *
 * A `<template>`'s content is an inert document fragment, so scripts do not run and `<img>`
 * resources are not fetched while parsing. Never swap this for `innerHTML` on a live or merely
 * detached element, which is neither.
 * @param {string} html
 * @param {object} policy
 * @returns {string}
 */
function sanitizePass(html, policy) {
  let template = document.createElement('template');

  template.innerHTML = html;

  sanitizeChildren(template.content, policy);

  return template.innerHTML;
}

/**
 * Reduces a renderable value to the string to sanitize.
 *
 * A `SafeString` is unwrapped rather than trusted. `htmlSafe` used to accept one and stringify it,
 * so anything already marked safe upstream -- an `ember-intl` translation, a computed fragment --
 * has to keep rendering, but its "already trusted" claim is deliberately not honored: it goes
 * through the policy like any other string. That is stricter than the behavior it replaces, not
 * looser.
 *
 * Anything with no meaningful text form (objects, arrays, booleans, `NaN`) is not content and
 * becomes `''`.
 * @param {unknown} value
 * @returns {string}
 */
function coerceToString(value) {
  if (typeof value === 'string') return value;

  if (typeof value?.toHTML === 'function') return value.toHTML();

  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
}

/**
 * Runs passes until the output stops changing.
 * @param {string} html
 * @param {object} policy
 * @returns {string|null} The stable output, or `null` when it never converged.
 */
function sanitizeUntilStable(html, policy) {
  let current = html;

  for (let pass = 0; pass < MAX_SANITIZE_PASSES; pass++) {
    let next = sanitizePass(current, policy);

    if (next === current) return next;

    current = next;
  }

  return null;
}

/**
 * Sanitizes a value under the given policy.
 * @param {unknown} value - Consumer-supplied content. Strings, `SafeString`s, and finite numbers
 *   are sanitized; anything else returns `''`.
 * @param {object} [policy] - One of `STRICT` or `RICH` from `utils/spds/html-verification`. Defaults
 *   to `RICH`.
 * @returns {string} Plain string, never a `SafeString`. Use the `spds-sanitize-html` helper to mark
 *   the result as trusted.
 */
export function sanitize(value, policy = RICH) {
  let html = coerceToString(value);

  // Fast path. Without a `<` or an `&` the string cannot carry markup or an entity, and the
  // container getter this runs in is on the hot path for essentially all SPDS text rendering.
  if (!html || !(html.includes('<') || html.includes('&'))) return html;

  if (!isParsingSupported()) return escapeHtml(html);

  // Never converging means hostile by construction, so fail closed and render the original as
  // visible text rather than returning markup no pass has vouched for.
  return sanitizeUntilStable(html, policy) ?? escapeHtml(html);
}

export default { sanitize };
